import * as z from 'zod';

export const CreateScheduledDataWorkflowRequest = z.object({
  scheduledDataWorkflow: z
    .object({
      _id: z
        .string()
        .describe('Scheduled data workflow ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Scheduled data workflow name.')
        .max(1024)
        .optional(),
      enabled: z
        .boolean()
        .describe(
          "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
        )
        .optional(),
      cronExpression: z
        .string()
        .describe(
          '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
        )
        .max(200)
        .optional(),
      lastExecutionDate: z
        .date()
        .describe(
          'Date and time when the scheduled data movement workflow was last run.'
        )
        .optional()
        .nullable(),
      nextExecutionDate: z
        .date()
        .describe(
          'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
        )
        .optional()
        .nullable(),
      workflow: z
        .object({
          steps: z
            .array(
              z.intersection(
                z.object({
                  source: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z.never().optional(),
                        }),
                        z.object({
                          localization: z.never().optional(),
                          file: z.never().optional(),
                          wixDataCollection: z
                            .object({
                              collectionId: z
                                .string()
                                .describe('ID of the data collection.')
                                .min(1)
                                .max(256)
                                .optional(),
                              filter: z
                                .record(z.string(), z.any())
                                .describe(
                                  'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                                )
                                .optional()
                                .nullable(),
                              fields: z.array(z.string()).optional(),
                            })
                            .describe(
                              'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          file: z.never().optional(),
                          localization: z
                            .object({
                              languages: z
                                .array(z.string())
                                .min(2)
                                .max(64)
                                .optional(),
                            })
                            .describe(
                              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z
                            .object({
                              format: z
                                .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                .describe(
                                  'File format. If not specified, automatically determined by Wix.'
                                )
                                .optional(),
                              fileId: z
                                .string()
                                .describe(
                                  "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                                )
                                .max(512)
                                .optional(),
                              url: z
                                .string()
                                .describe(
                                  'URL where the source file is uploaded. Automatically generated by Wix.'
                                )
                                .max(2048)
                                .optional(),
                            })
                            .describe(
                              'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                            ),
                        }),
                      ])
                    )
                    .describe('Source to read data from.')
                    .optional(),
                  destination: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z.never().optional(),
                        }),
                        z.object({
                          localization: z.never().optional(),
                          file: z.never().optional(),
                          wixDataCollection: z
                            .object({
                              collectionId: z
                                .string()
                                .describe('ID of the destination collection.')
                                .max(256)
                                .optional(),
                              writePolicy: z
                                .enum([
                                  'OVERWRITE',
                                  'SKIP_EXISTING',
                                  'TRUNCATE_BEFORE',
                                ])
                                .describe(
                                  'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          file: z.never().optional(),
                          localization: z
                            .object({
                              languages: z
                                .array(z.string())
                                .min(2)
                                .max(64)
                                .optional(),
                            })
                            .describe(
                              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z
                            .object({
                              format: z
                                .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                .describe('File format.')
                                .optional(),
                              fileName: z
                                .string()
                                .describe('File name.')
                                .max(512)
                                .optional()
                                .nullable(),
                              url: z
                                .string()
                                .describe('File download URL.')
                                .max(2048)
                                .optional(),
                            })
                            .describe(
                              'Downloadable file with the moved items.'
                            ),
                        }),
                      ])
                    )
                    .describe('Destination to write data to.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    sourceSiteId: z.never().optional(),
                    destinationSiteId: z.never().optional(),
                  }),
                  z.object({
                    destinationSiteId: z.never().optional(),
                    sourceSiteId: z
                      .string()
                      .describe(
                        'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    sourceSiteId: z.never().optional(),
                    destinationSiteId: z
                      .string()
                      .describe(
                        'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
            )
            .min(1)
            .max(100),
        })
        .describe(
          'Ordered workflow specification describing what should run when the schedule fires.'
        ),
      lastWorkflowId: z
        .string()
        .describe(
          'ID of the data movement workflow created by the most recent execution.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe('Scheduled data movement workflow to create.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['NEXT_SCHEDULE_EXECUTION']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const CreateScheduledDataWorkflowResponse = z.object({
  _id: z
    .string()
    .describe('Scheduled data workflow ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Scheduled data workflow name.')
    .max(1024)
    .optional(),
  enabled: z
    .boolean()
    .describe(
      "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
    )
    .optional(),
  cronExpression: z
    .string()
    .describe(
      '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
    )
    .max(200)
    .optional(),
  lastExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow was last run.'
    )
    .optional()
    .nullable(),
  nextExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
    )
    .optional()
    .nullable(),
  workflow: z
    .object({
      steps: z
        .array(
          z.intersection(
            z.object({
              source: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the data collection.')
                            .min(1)
                            .max(256)
                            .optional(),
                          filter: z
                            .record(z.string(), z.any())
                            .describe(
                              'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                            )
                            .optional()
                            .nullable(),
                          fields: z.array(z.string()).optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe(
                              'File format. If not specified, automatically determined by Wix.'
                            )
                            .optional(),
                          fileId: z
                            .string()
                            .describe(
                              "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                            )
                            .max(512)
                            .optional(),
                          url: z
                            .string()
                            .describe(
                              'URL where the source file is uploaded. Automatically generated by Wix.'
                            )
                            .max(2048)
                            .optional(),
                        })
                        .describe(
                          'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                        ),
                    }),
                  ])
                )
                .describe('Source to read data from.')
                .optional(),
              destination: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the destination collection.')
                            .max(256)
                            .optional(),
                          writePolicy: z
                            .enum([
                              'OVERWRITE',
                              'SKIP_EXISTING',
                              'TRUNCATE_BEFORE',
                            ])
                            .describe(
                              'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe('File format.')
                            .optional(),
                          fileName: z
                            .string()
                            .describe('File name.')
                            .max(512)
                            .optional()
                            .nullable(),
                          url: z
                            .string()
                            .describe('File download URL.')
                            .max(2048)
                            .optional(),
                        })
                        .describe('Downloadable file with the moved items.'),
                    }),
                  ])
                )
                .describe('Destination to write data to.')
                .optional(),
            }),
            z.xor([
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z.never().optional(),
              }),
              z.object({
                destinationSiteId: z.never().optional(),
                sourceSiteId: z
                  .string()
                  .describe(
                    'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z
                  .string()
                  .describe(
                    'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
        )
        .min(1)
        .max(100)
        .optional(),
    })
    .describe(
      'Ordered workflow specification describing what should run when the schedule fires.'
    )
    .optional(),
  lastWorkflowId: z
    .string()
    .describe(
      'ID of the data movement workflow created by the most recent execution.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const RescheduleScheduledDataWorkflowRequest = z.object({
  scheduledDataWorkflowId: z
    .string()
    .describe('ID of the scheduled data movement workflow to reschedule.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['NEXT_SCHEDULE_EXECUTION']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const RescheduleScheduledDataWorkflowResponse = z.object({
  nextExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow is next scheduled to run.'
    )
    .optional()
    .nullable(),
});
export const TriggerScheduledDataWorkflowNowRequest = z.object({
  scheduledDataWorkflowId: z
    .string()
    .describe('ID of the scheduled data movement workflow to run immediately.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const TriggerScheduledDataWorkflowNowResponse = z.object({});
export const UpdateScheduledDataWorkflowRequest = z.object({
  scheduledDataWorkflow: z
    .object({
      _id: z
        .string()
        .describe('Scheduled data workflow ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Scheduled data workflow name.')
        .max(1024)
        .optional(),
      enabled: z
        .boolean()
        .describe(
          "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
        )
        .optional(),
      cronExpression: z
        .string()
        .describe(
          '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
        )
        .max(200)
        .optional(),
      lastExecutionDate: z
        .date()
        .describe(
          'Date and time when the scheduled data movement workflow was last run.'
        )
        .optional()
        .nullable(),
      nextExecutionDate: z
        .date()
        .describe(
          'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
        )
        .optional()
        .nullable(),
      workflow: z
        .object({
          steps: z
            .array(
              z.intersection(
                z.object({
                  source: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z.never().optional(),
                        }),
                        z.object({
                          localization: z.never().optional(),
                          file: z.never().optional(),
                          wixDataCollection: z
                            .object({
                              collectionId: z
                                .string()
                                .describe('ID of the data collection.')
                                .min(1)
                                .max(256)
                                .optional(),
                              filter: z
                                .record(z.string(), z.any())
                                .describe(
                                  'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                                )
                                .optional()
                                .nullable(),
                              fields: z.array(z.string()).optional(),
                            })
                            .describe(
                              'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          file: z.never().optional(),
                          localization: z
                            .object({
                              languages: z
                                .array(z.string())
                                .min(2)
                                .max(64)
                                .optional(),
                            })
                            .describe(
                              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z
                            .object({
                              format: z
                                .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                .describe(
                                  'File format. If not specified, automatically determined by Wix.'
                                )
                                .optional(),
                              fileId: z
                                .string()
                                .describe(
                                  "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                                )
                                .max(512)
                                .optional(),
                              url: z
                                .string()
                                .describe(
                                  'URL where the source file is uploaded. Automatically generated by Wix.'
                                )
                                .max(2048)
                                .optional(),
                            })
                            .describe(
                              'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                            ),
                        }),
                      ])
                    )
                    .describe('Source to read data from.')
                    .optional(),
                  destination: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z.never().optional(),
                        }),
                        z.object({
                          localization: z.never().optional(),
                          file: z.never().optional(),
                          wixDataCollection: z
                            .object({
                              collectionId: z
                                .string()
                                .describe('ID of the destination collection.')
                                .max(256)
                                .optional(),
                              writePolicy: z
                                .enum([
                                  'OVERWRITE',
                                  'SKIP_EXISTING',
                                  'TRUNCATE_BEFORE',
                                ])
                                .describe(
                                  'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          file: z.never().optional(),
                          localization: z
                            .object({
                              languages: z
                                .array(z.string())
                                .min(2)
                                .max(64)
                                .optional(),
                            })
                            .describe(
                              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                            ),
                        }),
                        z.object({
                          wixDataCollection: z.never().optional(),
                          localization: z.never().optional(),
                          file: z
                            .object({
                              format: z
                                .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                .describe('File format.')
                                .optional(),
                              fileName: z
                                .string()
                                .describe('File name.')
                                .max(512)
                                .optional()
                                .nullable(),
                              url: z
                                .string()
                                .describe('File download URL.')
                                .max(2048)
                                .optional(),
                            })
                            .describe(
                              'Downloadable file with the moved items.'
                            ),
                        }),
                      ])
                    )
                    .describe('Destination to write data to.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    sourceSiteId: z.never().optional(),
                    destinationSiteId: z.never().optional(),
                  }),
                  z.object({
                    destinationSiteId: z.never().optional(),
                    sourceSiteId: z
                      .string()
                      .describe(
                        'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    sourceSiteId: z.never().optional(),
                    destinationSiteId: z
                      .string()
                      .describe(
                        'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
            )
            .min(1)
            .max(100),
        })
        .describe(
          'Ordered workflow specification describing what should run when the schedule fires.'
        ),
      lastWorkflowId: z
        .string()
        .describe(
          'ID of the data movement workflow created by the most recent execution.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe('Scheduled data movement workflow information to update.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['NEXT_SCHEDULE_EXECUTION']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const UpdateScheduledDataWorkflowResponse = z.object({
  _id: z
    .string()
    .describe('Scheduled data workflow ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Scheduled data workflow name.')
    .max(1024)
    .optional(),
  enabled: z
    .boolean()
    .describe(
      "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
    )
    .optional(),
  cronExpression: z
    .string()
    .describe(
      '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
    )
    .max(200)
    .optional(),
  lastExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow was last run.'
    )
    .optional()
    .nullable(),
  nextExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
    )
    .optional()
    .nullable(),
  workflow: z
    .object({
      steps: z
        .array(
          z.intersection(
            z.object({
              source: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the data collection.')
                            .min(1)
                            .max(256)
                            .optional(),
                          filter: z
                            .record(z.string(), z.any())
                            .describe(
                              'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                            )
                            .optional()
                            .nullable(),
                          fields: z.array(z.string()).optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe(
                              'File format. If not specified, automatically determined by Wix.'
                            )
                            .optional(),
                          fileId: z
                            .string()
                            .describe(
                              "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                            )
                            .max(512)
                            .optional(),
                          url: z
                            .string()
                            .describe(
                              'URL where the source file is uploaded. Automatically generated by Wix.'
                            )
                            .max(2048)
                            .optional(),
                        })
                        .describe(
                          'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                        ),
                    }),
                  ])
                )
                .describe('Source to read data from.')
                .optional(),
              destination: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the destination collection.')
                            .max(256)
                            .optional(),
                          writePolicy: z
                            .enum([
                              'OVERWRITE',
                              'SKIP_EXISTING',
                              'TRUNCATE_BEFORE',
                            ])
                            .describe(
                              'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe('File format.')
                            .optional(),
                          fileName: z
                            .string()
                            .describe('File name.')
                            .max(512)
                            .optional()
                            .nullable(),
                          url: z
                            .string()
                            .describe('File download URL.')
                            .max(2048)
                            .optional(),
                        })
                        .describe('Downloadable file with the moved items.'),
                    }),
                  ])
                )
                .describe('Destination to write data to.')
                .optional(),
            }),
            z.xor([
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z.never().optional(),
              }),
              z.object({
                destinationSiteId: z.never().optional(),
                sourceSiteId: z
                  .string()
                  .describe(
                    'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z
                  .string()
                  .describe(
                    'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
        )
        .min(1)
        .max(100)
        .optional(),
    })
    .describe(
      'Ordered workflow specification describing what should run when the schedule fires.'
    )
    .optional(),
  lastWorkflowId: z
    .string()
    .describe(
      'ID of the data movement workflow created by the most recent execution.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const DeleteScheduledDataWorkflowRequest = z.object({
  scheduledDataWorkflowId: z
    .string()
    .describe('ID of the scheduled data movement workflow to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteScheduledDataWorkflowResponse = z.object({});
export const GetScheduledDataWorkflowRequest = z.object({
  scheduledDataWorkflowId: z
    .string()
    .describe('ID of the scheduled data movement workflow to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['NEXT_SCHEDULE_EXECUTION']))
        .max(1)
        .optional(),
    })
    .optional(),
});
export const GetScheduledDataWorkflowResponse = z.object({
  _id: z
    .string()
    .describe('Scheduled data workflow ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Scheduled data workflow name.')
    .max(1024)
    .optional(),
  enabled: z
    .boolean()
    .describe(
      "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
    )
    .optional(),
  cronExpression: z
    .string()
    .describe(
      '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
    )
    .max(200)
    .optional(),
  lastExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow was last run.'
    )
    .optional()
    .nullable(),
  nextExecutionDate: z
    .date()
    .describe(
      'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
    )
    .optional()
    .nullable(),
  workflow: z
    .object({
      steps: z
        .array(
          z.intersection(
            z.object({
              source: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the data collection.')
                            .min(1)
                            .max(256)
                            .optional(),
                          filter: z
                            .record(z.string(), z.any())
                            .describe(
                              'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                            )
                            .optional()
                            .nullable(),
                          fields: z.array(z.string()).optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe(
                              'File format. If not specified, automatically determined by Wix.'
                            )
                            .optional(),
                          fileId: z
                            .string()
                            .describe(
                              "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                            )
                            .max(512)
                            .optional(),
                          url: z
                            .string()
                            .describe(
                              'URL where the source file is uploaded. Automatically generated by Wix.'
                            )
                            .max(2048)
                            .optional(),
                        })
                        .describe(
                          'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                        ),
                    }),
                  ])
                )
                .describe('Source to read data from.')
                .optional(),
              destination: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z.never().optional(),
                    }),
                    z.object({
                      localization: z.never().optional(),
                      file: z.never().optional(),
                      wixDataCollection: z
                        .object({
                          collectionId: z
                            .string()
                            .describe('ID of the destination collection.')
                            .max(256)
                            .optional(),
                          writePolicy: z
                            .enum([
                              'OVERWRITE',
                              'SKIP_EXISTING',
                              'TRUNCATE_BEFORE',
                            ])
                            .describe(
                              'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      file: z.never().optional(),
                      localization: z
                        .object({
                          languages: z
                            .array(z.string())
                            .min(2)
                            .max(64)
                            .optional(),
                        })
                        .describe(
                          'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                        ),
                    }),
                    z.object({
                      wixDataCollection: z.never().optional(),
                      localization: z.never().optional(),
                      file: z
                        .object({
                          format: z
                            .enum(['UNKNOWN', 'CSV', 'JSONL'])
                            .describe('File format.')
                            .optional(),
                          fileName: z
                            .string()
                            .describe('File name.')
                            .max(512)
                            .optional()
                            .nullable(),
                          url: z
                            .string()
                            .describe('File download URL.')
                            .max(2048)
                            .optional(),
                        })
                        .describe('Downloadable file with the moved items.'),
                    }),
                  ])
                )
                .describe('Destination to write data to.')
                .optional(),
            }),
            z.xor([
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z.never().optional(),
              }),
              z.object({
                destinationSiteId: z.never().optional(),
                sourceSiteId: z
                  .string()
                  .describe(
                    'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                sourceSiteId: z.never().optional(),
                destinationSiteId: z
                  .string()
                  .describe(
                    'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
        )
        .min(1)
        .max(100)
        .optional(),
    })
    .describe(
      'Ordered workflow specification describing what should run when the schedule fires.'
    )
    .optional(),
  lastWorkflowId: z
    .string()
    .describe(
      'ID of the data movement workflow created by the most recent execution.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const ListScheduledDataWorkflowsRequest = z.object({
  options: z
    .object({
      fields: z
        .array(z.enum(['NEXT_SCHEDULE_EXECUTION']))
        .max(1)
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Reserved for future server-side paging support. Currently ignored.'
        )
        .optional(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Reserved for future server-side filtering support. Currently ignored.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListScheduledDataWorkflowsResponse = z.object({
  scheduledDataWorkflows: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Scheduled data workflow ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number that increments by 1 each time the scheduled data workflow updates.\nTo prevent conflicting changes, pass the current revision when updating.\n\nIgnored when creating a scheduled data workflow.'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Scheduled data workflow name.')
          .max(1024)
          .optional(),
        enabled: z
          .boolean()
          .describe(
            "Whether the scheduled data workflow is enabled for execution.\n\nWhen `true`, it runs according to the schedule defined by `cronExpression`. When `false`, it doesn't run."
          )
          .optional(),
        cronExpression: z
          .string()
          .describe(
            '[Cron expression](https://en.wikipedia.org/wiki/Cron#Cron_expression) defining when the scheduled data workflow should run.\n\nUses standard 5-field cron format (minute, hour, day of month, month, day of week) in [UTC](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). Supports using `H` for minute randomization. For example, `0 2 * * *` runs daily at 2\\:00 AM UTC, or `H 2 * * *` runs daily at 2\\:XX AM UTC where XX is a random minute.'
          )
          .max(200)
          .optional(),
        lastExecutionDate: z
          .date()
          .describe(
            'Date and time when the scheduled data movement workflow was last run.'
          )
          .optional()
          .nullable(),
        nextExecutionDate: z
          .date()
          .describe(
            'Date and time when the scheduled data movement workflow is scheduled to run next.\n\nCalculated based on the cron expression and current time. Returned only when the request includes `nextScheduleExecution` in the `fields` parameter.'
          )
          .optional()
          .nullable(),
        workflow: z
          .object({
            steps: z
              .array(
                z.intersection(
                  z.object({
                    source: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({
                            wixDataCollection: z.never().optional(),
                            localization: z.never().optional(),
                            file: z.never().optional(),
                          }),
                          z.object({
                            localization: z.never().optional(),
                            file: z.never().optional(),
                            wixDataCollection: z
                              .object({
                                collectionId: z
                                  .string()
                                  .describe('ID of the data collection.')
                                  .min(1)
                                  .max(256)
                                  .optional(),
                                filter: z
                                  .record(z.string(), z.any())
                                  .describe(
                                    'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                                  )
                                  .optional()
                                  .nullable(),
                                fields: z.array(z.string()).optional(),
                              })
                              .describe(
                                'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                              ),
                          }),
                          z.object({
                            wixDataCollection: z.never().optional(),
                            file: z.never().optional(),
                            localization: z
                              .object({
                                languages: z
                                  .array(z.string())
                                  .min(2)
                                  .max(64)
                                  .optional(),
                              })
                              .describe(
                                'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                              ),
                          }),
                          z.object({
                            wixDataCollection: z.never().optional(),
                            localization: z.never().optional(),
                            file: z
                              .object({
                                format: z
                                  .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                  .describe(
                                    'File format. If not specified, automatically determined by Wix.'
                                  )
                                  .optional(),
                                fileId: z
                                  .string()
                                  .describe(
                                    "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                                  )
                                  .max(512)
                                  .optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'URL where the source file is uploaded. Automatically generated by Wix.'
                                  )
                                  .max(2048)
                                  .optional(),
                              })
                              .describe(
                                'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                              ),
                          }),
                        ])
                      )
                      .describe('Source to read data from.')
                      .optional(),
                    destination: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({
                            wixDataCollection: z.never().optional(),
                            localization: z.never().optional(),
                            file: z.never().optional(),
                          }),
                          z.object({
                            localization: z.never().optional(),
                            file: z.never().optional(),
                            wixDataCollection: z
                              .object({
                                collectionId: z
                                  .string()
                                  .describe('ID of the destination collection.')
                                  .max(256)
                                  .optional(),
                                writePolicy: z
                                  .enum([
                                    'OVERWRITE',
                                    'SKIP_EXISTING',
                                    'TRUNCATE_BEFORE',
                                  ])
                                  .describe(
                                    'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                              ),
                          }),
                          z.object({
                            wixDataCollection: z.never().optional(),
                            file: z.never().optional(),
                            localization: z
                              .object({
                                languages: z
                                  .array(z.string())
                                  .min(2)
                                  .max(64)
                                  .optional(),
                              })
                              .describe(
                                'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                              ),
                          }),
                          z.object({
                            wixDataCollection: z.never().optional(),
                            localization: z.never().optional(),
                            file: z
                              .object({
                                format: z
                                  .enum(['UNKNOWN', 'CSV', 'JSONL'])
                                  .describe('File format.')
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('File name.')
                                  .max(512)
                                  .optional()
                                  .nullable(),
                                url: z
                                  .string()
                                  .describe('File download URL.')
                                  .max(2048)
                                  .optional(),
                              })
                              .describe(
                                'Downloadable file with the moved items.'
                              ),
                          }),
                        ])
                      )
                      .describe('Destination to write data to.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      sourceSiteId: z.never().optional(),
                      destinationSiteId: z.never().optional(),
                    }),
                    z.object({
                      destinationSiteId: z.never().optional(),
                      sourceSiteId: z
                        .string()
                        .describe(
                          'When moving data from the Wix data collection of a site other than the current site, the ID of the site to read from.\n\nBy default, the `source` site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `source` site or a `destination` site other than the current site, but not both.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                    z.object({
                      sourceSiteId: z.never().optional(),
                      destinationSiteId: z
                        .string()
                        .describe(
                          'When moving data to the Wix data collection of a site other than the current site, the ID of the site to write to.\n\nBy default, the `destination` site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of a `destination` site or a `source` site other than the current site, but not both.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(100)
              .optional(),
          })
          .describe(
            'Ordered workflow specification describing what should run when the schedule fires.'
          )
          .optional(),
        lastWorkflowId: z
          .string()
          .describe(
            'ID of the data movement workflow created by the most recent execution.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Paging metadata, when available. Currently omitted because server-side paging is not applied.'
    )
    .optional(),
});
